import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, X } from 'lucide-react';
import { useExtensionStore, getBridge } from './store';
import { aggregateContributions } from './registry';
import ExtensionTreeSection from './tree/ExtensionTreeSection';
import OsScroll from '../components/ui/OsScroll';
import useSidebarFilterShortcut from './useSidebarFilterShortcut';
import { resolveActionIcon } from './tree/icons';
import Tooltip from '../components/ui/Tooltip';
import './extSidebarActions.css';

/**
 * Mounts the ExtensionTreeSection for whichever extension owns `panelId` —
 * i.e. only while that extension's own tab is open, never inside Collections.
 * Reverse-looks-up activityBarId from the panel id (ActivityBar.js sets
 * `activeTab` to a panel id, not an activityBarId) to find the matching
 * `sidebarSections` contribution. Renders nothing if that extension declares
 * no sidebar section.
 *
 * Layout deliberately mirrors Sidebar.js's Collections column exactly — a
 * fixed search-row header (same classes, same border) above the scroll area —
 * so switching between Collections and an extension panel doesn't visually
 * jump. The search input therefore lives here, not inside the tree section.
 */
export default function ExtensionSidebarSections({ panelId, workspaceId }) {
  const { t } = useTranslation();
  const extensions = useExtensionStore((s) => s.extensions);
  const enabled = useExtensionStore((s) => s.enabled);

  const [searchTerm, setSearchTerm] = useState('');
  const clearSearch = useCallback(() => setSearchTerm(''), []);
  const rootRef = useRef(null);
  const filterRef = useRef(null);
  useSidebarFilterShortcut(rootRef, filterRef);

  const section = useMemo(() => {
    const entries = Object.keys(extensions).map((id) => ({
      manifest: extensions[id],
      enabled: !!enabled[id],
    }));
    const { panelsByActivityBarId, sidebarSectionsByActivityBarId } = aggregateContributions(entries);
    const activityBarId = [...panelsByActivityBarId.entries()]
      .find(([, panel]) => panel.id === panelId)?.[0];
    if (!activityBarId) return null;
    return sidebarSectionsByActivityBarId.get(activityBarId) || null;
  }, [extensions, enabled, panelId]);

  // The section's "create" affordance, by the same convention the title bar
  // uses: the header action drawn with a plus. Rendering it here as a labelled
  // button (rather than only as a 14px icon up in the window chrome) is the
  // point — the icon was unlabelled and nowhere near the list it adds to.
  const createAction = useMemo(
    () => (section?.headerActions || []).find((a) => a.icon === 'plus') || null,
    [section]
  );

  // Every other header action (DB Manager: Refresh) sits at the end of the
  // same row since the title bar shows no page actions (owner, 20.10.2026).
  const otherActions = useMemo(() => (section?.headerActions || []).filter((a) => a.icon !== 'plus'), [section]);
  const runAction = (actionId) =>
    getBridge(section.extensionId)?.postToGuest('__section_action__', { sectionId: section.id, actionId });

  if (!section) return null;

  return (
    <div ref={rootRef} className="flex flex-col flex-1 min-h-0">
      <div className="flex-shrink-0 px-2 pt-2 border-b border-gray-200">
        <div className="sidebar-panel-search-row">
          <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
          <input
            ref={filterRef}
            type="text"
            placeholder={`Filter ${section.title.toLowerCase()}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
            autoComplete="off"
          />
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
            {searchTerm ? (
              <button
                type="button"
                onClick={clearSearch}
                className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                aria-label="Clear filter"
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {(createAction || otherActions.length > 0) && (
        <div className="ext-sidebar-new-row">
          {createAction && (
            <button type="button" className="ext-sidebar-new-btn" onClick={() => runAction(createAction.id)}>
              <Plus size={13} aria-hidden /> {createAction.tooltip}
            </button>
          )}
          {otherActions.map((a) => {
            const ActionIcon = resolveActionIcon(a.icon);
            return (
              <Tooltip key={a.id} content={a.tooltip} side="bottom">
                <button type="button" className="ext-sidebar-icon-btn" aria-label={a.tooltip} onClick={() => runAction(a.id)}>
                  <ActionIcon size={14} aria-hidden />
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}
      {/* Small explicit left inset (the curated CSS has no pl-0.5/pl-1 utility,
          so a class would resolve to 0 and the rows' hover border would touch
          the icon rail). 6px keeps top-level rows off the rail while still
          seating their expand caret close to the search icon above. */}
      <OsScroll className="flex-1 min-h-0 pr-2 pb-3" style={{ paddingLeft: 6 }}>
        {/* Empty-state copy is shared by every contributed sidebar section.
            It is phrased for connections because db-manager is the ONLY
            extension contributing a sidebarSection today (the six other
            bundled extensions are host-rendered and contribute none, and the
            marketplace is feature-flagged off) — so there is no second reader
            to be wrong for. `extensionSidebarEmptyState.test.js` pins that
            premise: the day a second section ships, it fails and this copy has
            to become per-section rather than silently mislabelling the new
            one. The ICON is already per-section via the manifest. */}
        <ExtensionTreeSection
          key={section.id}
          extensionId={section.extensionId}
          sectionId={section.id}
          workspaceId={workspaceId}
          searchTerm={searchTerm}
          emptyIcon={section.icon}
          emptyTitle={t('extensions.sidebarEmptyTitle')}
          emptyHint={t('extensions.sidebarEmptyHint')}
        />
      </OsScroll>
    </div>
  );
}
